import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useUserTenants } from '../hooks/useUserTenants'
import type { TenantEntry } from '../services/schemas'
import { TenantContext } from './schemaContextStore'

/**
 * Provides the list of accessible tenants and the currently selected tenant
 * to all descendant components. Auto-selects the first schema once the list loads.
 *
 * Must be rendered inside an authenticated context.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const { schemas, isLoading, error } = useUserTenants()
  const [selectedTenantName, setSelectedTenantName] = useState<string | null>(null)

  const selectedTenant = useMemo(
    () => schemas.find((tenant) => tenant.schemaName === selectedTenantName) ?? schemas[0] ?? null,
    [schemas, selectedTenantName],
  )

  const setSelectedTenant = useCallback((tenant: TenantEntry) => {
    setSelectedTenantName(tenant.schemaName)
  }, [])

  return (
    <TenantContext.Provider value={{ schemas, selectedTenant, setSelectedTenant, isLoading, error }}>
      {children}
    </TenantContext.Provider>
  )
}
